import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home,
  Database,
  HardDrive,
  ShieldCheck,
  LogOut,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// onClose is optional so this component still works even if a parent
// hasn't been updated to pass it yet — but wiring it up (see note below
// the component) is what makes menu clicks auto-close the sidebar on mobile.
const Sidebar = ({ open, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const menus = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: <Home size={20} />,
    },
    {
      name: "Backup Data",
      path: "/backup-data",
      icon: <Database size={20} />,
    },
    {
      name: "Backup Outlet Master",
      path: "/backup-outlet-master",
      // Was also Database before — gave it its own icon so the two
      // backup-related links are visually distinguishable at a glance.
      icon: <HardDrive size={20} />,
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  // Closes the sidebar after navigating, but only matters on mobile —
  // md:translate-x-0 keeps it permanently visible on desktop regardless.
  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-screen w-72 bg-slate-950 text-white z-50
      transition-transform duration-300 transform flex flex-col
      ${open ? "translate-x-0" : "-translate-x-full"}
      md:translate-x-0`}
    >
      {/* Logo */}
      <div className="h-20 flex-shrink-0 border-b border-slate-800 flex items-center justify-between px-6">
        <div className="flex items-center min-w-0">
          <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-blue-600 flex items-center justify-center">
            <ShieldCheck size={24} />
          </div>

          <div className="ml-3 min-w-0">
            <h1 className="font-bold text-lg truncate">Backup Manager</h1>
            <p className="text-xs text-slate-400 truncate">
              Data Protection System
            </p>
          </div>
        </div>

        {/* Close button - mobile only, lets users dismiss without tapping the overlay */}
        <button
          onClick={onClose}
          className="md:hidden p-1.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition flex-shrink-0"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      {/* Menu - scrollable with hidden scrollbar in case the list grows past viewport height */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-6">
        <p className="text-xs uppercase tracking-wider text-slate-500 px-3 mb-4">
          Main Menu
        </p>

        <ul className="space-y-2">
          {menus.map((menu) => (
            <li key={menu.path}>
              <NavLink
                to={menu.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                  ${
                    isActive
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Active indicator bar - a subtle extra cue beyond just the background color */}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-white/90" />
                    )}
                    {menu.icon}
                    <span className="font-medium">{menu.name}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-800 p-4">
        <div className="bg-slate-900 rounded-xl p-3 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.userName || "Guest User"}
            </p>
            <p className="text-xs text-slate-400 truncate">
              {user?.mobileNo || "Backup Manager v1.0"}
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="flex-shrink-0 p-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition"
            aria-label="Logout"
            title="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;